import {
    defaultFilterVert,
    Filter,
    type FilterSystem,
    GlProgram,
    Matrix,
    type RenderSurface,
    type Sprite,
    Texture,
    UniformGroup
} from "pixi.js";

import { Rgb } from "../../../shared/Settings";
import { SLOPE_UNIT, WAVE_GLSL, WAVE_TRAINS, WAVE_UNIFORMS, WaveTrain, waveVector } from "./Waves";

/**
 * The sea's whole look in one pass: the swell, the banded light on it, the glitter and the
 * whitecaps it breaks, the depth in its troughs, the wake bending the water, and the foam that
 * wake leaves — all resolved per pixel.
 *
 * **It is a drawing of water, not a simulation of it** (design/Water.md). The
 * light comes in five steps rather than a ramp, the glints are thresholded until they are either
 * there or not, and the foam has a hard edge and a lifted rim. Everything soft in here is soft
 * because leaving it hard aliased, not because water is.
 *
 * It replaces `SeaFilter`, which did the last two off a scrolling `displacement_map.png`. The map
 * is gone: the swell is **computed**, from the same [Waves.ts](Waves.ts) field the hulls riding on
 * it read, at each pixel's offset from the camera. One formula, two consumers — which is what lets
 * a boat be drawn sitting on the crest it looks like it is sitting on.
 *
 * **Where a pixel is, relative to the camera.** `uTrailMatrix` maps filter space into the trail
 * sprite's space, and that sprite is exactly the viewport — so the same matrix that finds the wake
 * also gives normalised screen coordinates, and `uSeaFrame` turns those into an offset from the
 * middle of the screen in world pixels. Two uses of one matrix rather than a second unrenderable
 * sprite carried around for its transform.
 *
 * **From the camera and not from the world**, because how far the camera has travelled lives in
 * each train's phase instead ([Waves.ts](Waves.ts)). That keeps every number here bounded however
 * long a battle runs, and costs the arena's seam nothing at all.
 *
 * **One map still comes in.** The trail is a texture [WakeField.ts](WakeField.ts) redraws every
 * frame, carrying two different things in one image: red and green are which way the water is
 * pushed, `0.5` being not at all, and blue is how much foam is on that pixel, accumulated
 * additively so overlapping marks build up. Foam is a **threshold** on that channel rather than
 * the channel itself, which is what makes it read as chunky water; the band inside the edge is
 * lifted into a rim and the disturbed water outside it darkened into a trough.
 *
 * Neither the trail sprite nor the plate may be rotated — the sea is axis-aligned and always will
 * be, so there is no rotation term here where the stock displacement filter has one.
 *
 * **WebGL only.** `SeaFilter` carried a WGSL twin that, by design/Water.md's own admission, had
 * never run: PixiJS tries WebGL first and this game sets no preference. Carrying an unverified
 * second copy of a shader this size — plus a second copy of `WAVE_GLSL`, which would then be
 * three implementations of one field — is exactly the drift the whole ocean design is arranged to
 * avoid. If WebGPU is ever wanted it should be written and *tested*, not carried along blind.
 */

/**
 * How the foam is cut out of the blue channel. Threshold is where foam starts, softness is how
 * far past that it takes to become solid, and the rim is how wide the lifted band inside the edge
 * is — all in the same units the channel is in, 0 to 1.
 *
 * These are **decisions, not dials**: they are what foam looks like, and the dashboard's job is
 * how much of it there is and how long it lasts. See design/Settings.md on where the line is.
 */
const FOAM_THRESHOLD = 0.18;
const FOAM_SOFTNESS = 0.22;
const FOAM_RIM = 0.3;
/**
 * How much the rim is lifted and the trough darkened, as a fraction. One number for both because
 * they are one effect — the relief that makes a patch of foam read as having a near edge.
 */
const FOAM_RELIEF = 0.22;

/**
 * Where a crest starts breaking and where it is fully broken, as a fraction of a full-strength
 * swell's height. Decisions in the same sense the foam's are: how much white there is on the sea
 * is `waveHeight` and the wind, but *that a wave breaks at its top* is what water does.
 *
 * High, because only the tops should go. Drop them and the whole sea turns white, which is the
 * single fastest way to make ships and loot unreadable — see design/Water.md's rules.
 *
 * **Placed against the field's own distribution**, which is the only way to place them: across the
 * shipped spectrum the height runs to 0.97 with its 97th percentile at 0.66, so these two put foam
 * on roughly the top one percent of the water. Set forty points lower they caught a sixth of the
 * sea, and a whitecap the size of a cloud is not a whitecap.
 */
const CAP_START = 0.62;
const CAP_FULL = 0.95;

/**
 * Where the light comes from, as a unit vector across the surface.
 *
 * Fixed rather than tied to anything in the world: a sea lit from a consistent quarter reads as
 * one surface, and there is no sun in this game to disagree with. Up and slightly left, so crests
 * facing north-west catch it — the same quarter the wave overlay has always drifted towards.
 *
 * **Exported, because the sea is not the only thing lit by it.** A hull's shadow falls the other
 * way from this, and a shadow disagreeing with the water it is cast on would be the one thing on
 * screen that says none of this is a surface. One definition — see `Boat.ride`.
 */
export const SEA_LIGHT = { x: -0.55, y: -0.84 };
/**
 * The two halves of the shading, as weights on a number that is then **banded and only then
 * scaled**. Both are chosen so the sum lands inside [-1, 1] on ordinary water and clips on the
 * biggest crests, which is what puts the band edges where the eye is looking.
 *
 * That order matters and cost a rebuild to find. Banding the *final* brightness quantises a range
 * of about a third of a stop into steps of a fifth, so the whole sea lands in one or two bands and
 * goes flat. Banding a normalised number and scaling afterwards puts a fixed number of steps
 * across the water however far `waveHeight` is turned down.
 *
 * `SHADE_LIGHT` is the **directional** half, and the one that makes the sea look lit rather than
 * merely striped. `SHADE_HEIGHT` is what stops it going flat on half the compass: a fixed light
 * against the slope produces nothing at all when the swell runs across it — the gradient is
 * perpendicular to the light and the dot product is zero. Height has no direction to be wrong
 * about, so it is the term that always reads.
 */
const SHADE_HEIGHT = 1.7;
const SHADE_LIGHT = 1.7;

/** How far either side of its own colour the water swings, at `waveHeight` of 1. */
const SHADE_GAIN = 0.78;

/**
 * How far the wave overlay is dragged along the surface's slope, in screen pixels at full
 * amplitude. This is what the displacement map used to do, and it is the term that keeps the
 * overlay's own detail reading as water *on* the swell rather than a pattern sliding over it.
 */
const SWELL_WARP = 46;

/**
 * How many steps the light is quantised into, across the whole range the shading covers.
 *
 * **The largest cartoon lever there is, and it costs one `floor`.** Banded light is what separates
 * a painted sea from a shaded one: real water blends, and a drawing of water does not. Five is
 * Counted per unit of a shading value that runs [-1, 1], so 2.5 is five steps across the water.
 * Enough to keep a crest reading as round, and few enough that the bands are unmistakably a
 * decision. Raise it far and the sea goes smooth again; drop it to three steps and the water turns
 * into a contour map.
 */
const SEA_BANDS = 2.5;

/**
 * How soft a band edge is, as a fraction of one band. Not zero, because a hard step aliases into
 * a crawling stair pattern wherever the gradient is shallow — which on a sea is most of it.
 *
 * A tenth, and it wants to stay about there. At a quarter the transition is over half the band
 * and the sea comes out as a soft gradient with no visible steps at all, which is the whole
 * effect gone — measured, on the first build of this phase.
 */
const BAND_SOFTNESS = 0.1;

/**
 * Where a face is steep enough toward the light to throw a glint, and where it is fully lit.
 *
 * Sun glitter, done off the **slope this shader already has** rather than a second noise field:
 * the spectrum's shortest train is fine and steep, so thresholding hard near the top of the
 * lighting term picks out its little faces and nothing else. Discrete glints rather than a sheen,
 * moving with the water for free because it *is* the water.
 *
 * The pair are close together on purpose. Widen the gap and the glints smear back into the
 * highlight they were cut out of.
 *
 * **Placed by measuring the field, not by taste.** Across the shipped spectrum the lighting
 * term runs [-0.50, 0.50] with its 97th percentile at 0.33 — so these two put glints on the
 * brightest few percent of the water and nowhere else. Guessed at first, they sat above the
 * maximum the term can reach and fired on nothing at all.
 */
const GLINT_START = 0.33;
const GLINT_FULL = 0.46;
/** How much white a glint adds. Small: this is a sparkle, and rule 8 is watching. */
const GLINT_STRENGTH = 0.28;

/**
 * The colour of deep water and of a crest arrive as **multipliers** on the plate underneath —
 * `deepTint` and `crestTint` in the options below.
 *
 * Multiplicative rather than a pair of flat colours mixed in, because the plate is not flat: the
 * wave overlay's own detail is in it, and replacing the colour would throw that away. Tinting
 * keeps every bit of it and still bends the sea green-deep in the troughs and pale toward the
 * crests, which is the depth cue a single colour scaled by brightness cannot give — a dark blue
 * and a dark green read as different distances, a dark blue and a light blue read as the same
 * water in shadow.
 *
 * **The deep end does the work.** The shipped plate is `#0090f4`, which has no red in it at all,
 * so a multiplier can take colour *out* and can never put any in: there is no pale a tint can
 * reach from that base. Pale is the banded light's job and the glints', and this is the green.
 *
 * They were two constants here until the Ocean panel existed. They are now derived from three
 * colours a designer picks — base, trough, crest — by `tintBetween` in shared/Settings.ts, which
 * is where that division and its one guard live. Nothing about the shader changed with them.
 */

const glFragment = `
// PixiJS injects mediump as the fragment default, and this shader works in **world** coordinates —
// thousands of pixels, where mediump lands on the nearest seven. Left alone the sea flattens into a
// soft gradient, and only on some drivers: the shared GLSL qualifies itself for the same reason,
// and this raises the default for everything else here that touches a world position.
precision highp float;

in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uTrailMap;

uniform vec4 uInputClamp;
uniform highp vec4 uInputSize;

uniform mat3 uTrailMatrix;
uniform highp vec4 uSeaFrame;
uniform vec4 uScales;
uniform vec4 uSurface;
uniform vec4 uFoamEdge;
uniform vec4 uFoamTint;
uniform vec4 uDeepTint;
uniform vec4 uCrestTint;

${WAVE_GLSL}

// Light in steps rather than a ramp. The smoothstep is what keeps a band edge from crawling
// across the shallow parts of the sea as a stair of aliased pixels; it is an anti-aliased floor
// and nothing more clever than that.
float band(float value)
{
    float scaled = value * ${SEA_BANDS.toFixed(1)};
    // Not named 'step': that is a GLSL builtin, and shadowing one is legal and a bad idea.
    float whole = floor(scaled);

    return (whole + smoothstep(0.5 - ${BAND_SOFTNESS.toFixed(3)}, 0.5 + ${BAND_SOFTNESS.toFixed(3)}, fract(scaled))) / ${SEA_BANDS.toFixed(1)};
}

void main(void)
{
    // The trail sprite is the viewport exactly, so its matrix doubles as the map from filter
    // space to normalised screen space — and uSeaFrame turns that into how far this pixel is from
    // the camera, in world pixels. Taken off the *unwarped* coordinate: the waves have to be
    // evaluated where the water really is before they are allowed to bend anything.
    vec2 viewUv = (uTrailMatrix * vec3(vTextureCoord, 1.0)).xy;
    vec3 sea = oceanWaves((viewUv - 0.5) * uSeaFrame.zw);

    // Slope, in units where a full-strength wave is about one rather than about a hundredth.
    vec2 slope = sea.yz * uScales.z;

    // The overlay is dragged down the face of the swell, which is the job the displacement map
    // used to do with a scrolling texture.
    vec2 rolled = vTextureCoord + uInputSize.zw * slope * uScales.x;

    // The trail is in that water, so it is read where the swell has carried it to rather than
    // where it was laid. Miss this and the foam alone sits still while the sea moves under it.
    vec2 trailUv = (uTrailMatrix * vec3(rolled, 1.0)).xy;
    vec4 trail = texture(uTrailMap, trailUv);

    vec2 pushed = rolled + uInputSize.zw * (trail.xy - 0.5) * uScales.w;
    vec4 color = texture(uTexture, clamp(pushed, uInputClamp.xy, uInputClamp.zw));

    // The shading, in two halves. Height is the one that always reads — a crest is pale and a
    // trough is dark whichever way the sea is running — and the fixed light across the slope is
    // what stops that from looking like painted stripes. The light alone would vanish on any swell
    // running across it, which is half the compass.
    float facing = dot(slope, uSurface.xy);

    // Banded, and **banded before it is scaled**: the quantised number runs [-1, 1] whatever the
    // dial says, so the same five steps cross the water on a calm sea and a wild one. Doing it the
    // other way round quantises a third of a stop into fifths and the sea comes out flat.
    float shade = clamp(sea.x * ${SHADE_HEIGHT.toFixed(2)} + facing * ${SHADE_LIGHT.toFixed(2)}, -1.0, 1.0);
    float lit = 1.0 + band(shade) * uScales.y;

    // Sun glitter: the little faces of the shortest train, turned hard toward the light. Cut out
    // of the same number the shading uses, so it costs a smoothstep and moves with the water
    // because it *is* the water. Left off the banding on purpose — a glint is the one thing on
    // this sea allowed to be a hard edge rather than a step.
    float glint = smoothstep(${GLINT_START.toFixed(3)}, ${GLINT_FULL.toFixed(3)}, facing) * uSurface.w;

    // And the tops break. A crest's white goes into the **same channel the wake's foam is in**,
    // so it comes out through the same threshold, the same rim and the same relief below: there is
    // one answer in this shader to what white water looks like, and a breaking crest gets it. It
    // is also why the two add up — a wake laid across a crest breaks harder than one in a trough,
    // which is what water does.
    float white = trail.z + smoothstep(${CAP_START.toFixed(3)}, ${CAP_FULL.toFixed(3)}, sea.x) * uSurface.z;

    // Foam is laid on after the water is bent, so it marks the disturbance without being
    // dragged around by it.
    float body = smoothstep(uFoamEdge.x, uFoamEdge.x + uFoamEdge.y, white);
    float rim = body - smoothstep(uFoamEdge.x + uFoamTint.a, uFoamEdge.x + uFoamTint.a + uFoamEdge.y, white);
    // The trough is the **wake's** alone, off trail.z rather than off the white above it. It is the
    // shadow a patch of foam casts in the water it is sitting in, and a wake really does sit in a
    // disturbance — a breaking crest does not, it is the top of a wave. Run the caps through this
    // as well and every crest is darkened by the near miss of its own foam, which reads as the sea
    // being brightest in its troughs: the exact opposite of what the light is saying.
    float trough = smoothstep(0.0, uFoamEdge.x, trail.z) * (1.0 - body);

    // Depth. A trough goes green-deep and a crest goes pale, as a tint on the plate rather than
    // a colour replacing it — the overlay's detail is in that plate and is worth keeping.
    vec3 depth = mix(uDeepTint.rgb, uCrestTint.rgb, band(sea.x * 0.5 + 0.5));

    vec3 foam = uFoamTint.rgb * color.a;
    vec3 water = clamp(color.rgb * lit * depth + glint * color.a, 0.0, color.a) * (1.0 - trough * uFoamEdge.w);

    finalColor = vec4(mix(water, foam, body * uFoamEdge.z) + foam * rim * uFoamEdge.w, color.a);
}
`;

/** What the sea is drawn through. See the notes at the top of this file. */
export interface OceanFilterOptions {
    /** The wake's map, red and green pushing the water and blue carrying foam. Must be in the scene. */
    trail: Sprite;
    /**
     * How tall the sea reads, 0 to 1 — `waveHeight` out of the dashboard.
     *
     * It scales the **effects** rather than the field: the warp, the light and the white. Scaling
     * the amplitude instead would move the height a crest breaks at every time the dial did, so a
     * calm sea would stop breaking not because it was calm but because the threshold had drifted
     * out from under it. This way a flat calm is simply a sea with nothing happening on it.
     */
    height: number;
    /** How white a breaking crest goes, 0 to 1, before `height` is taken out of it. */
    capStrength: number;
    /**
     * What the plate is multiplied by in a trough and on a crest — the sea's contrast. See the
     * note above; `Water` derives both from `config/ocean.json`.
     */
    deepTint: Rgb;
    crestTint: Rgb;
    /** Pixel amplitude of the wake's warp. Zero leaves the trail as foam and nothing else. */
    trailScale: number;
    /** Colour of the foam, packed RGB. Whitecaps borrow it — one white on the water. */
    foamColor: number;
    /** How solid the foam is where it is solid, 0 to 1. */
    foamStrength: number;
}

/**
 * A tint, packed, and **faded toward no tint at all as `waveHeight` comes down**.
 *
 * Every other effect in this shader scales with the dial, so a flat calm is a sea with nothing
 * happening on it. A depth ramp that did not would be the one thing still shouting on water that
 * had been turned off, which is not what the dial means.
 */
function tint(color: Rgb, height: number): Float32Array {
    return writeTint(new Float32Array(4), color, height);
}

/** The same, into a buffer the GPU is already reading — see `setSwell`. */
function writeTint(out: Float32Array, color: Rgb, height: number): Float32Array {
    out[0] = 1 + (color.r - 1) * height;
    out[1] = 1 + (color.g - 1) * height;
    out[2] = 1 + (color.b - 1) * height;
    out[3] = 0;

    return out;
}

export class OceanFilter extends Filter {
    private readonly _trail: Sprite;
    /** Scratch for packing a wave vector, so a per-frame uniform update allocates nothing. */
    private readonly _vector = { x: 0, y: 0 };

    /**
     * The three numbers the depth ramp is written from, kept because either half can be changed
     * without the other: `setSwell` moves the height, `setPalette` moves the colours, and both
     * have to rewrite the same two uniforms. In a battle neither is ever called twice.
     */
    private _height: number;
    private _deep: Rgb;
    private _crest: Rgb;

    constructor(options: OceanFilterOptions) {
        const uniforms = new UniformGroup({
            uTrailMatrix: { value: new Matrix(), type: "mat3x3<f32>" },
            /** Unused, unused, and how many world pixels of sea the viewport spans. */
            uSeaFrame: { value: new Float32Array([0, 0, 1, 1]), type: "vec4<f32>" },
            /** The swell's warp, how far the banded light swings, the slope's unit, the wake's warp. */
            uScales: {
                value: new Float32Array([SWELL_WARP * options.height, SHADE_GAIN * options.height, SLOPE_UNIT, options.trailScale]),
                type: "vec4<f32>"
            },
            /** Light direction x and y, how white the caps go, and how much a glint adds. */
            uSurface: {
                value: new Float32Array([
                    SEA_LIGHT.x,
                    SEA_LIGHT.y,
                    options.capStrength * options.height,
                    GLINT_STRENGTH * options.height
                ]),
                type: "vec4<f32>"
            },
            uFoamEdge: {
                value: new Float32Array([FOAM_THRESHOLD, FOAM_SOFTNESS, options.foamStrength, FOAM_RELIEF]),
                type: "vec4<f32>"
            },
            uFoamTint: {
                value: new Float32Array([
                    ((options.foamColor >> 16) & 0xff) / 255,
                    ((options.foamColor >> 8) & 0xff) / 255,
                    (options.foamColor & 0xff) / 255,
                    FOAM_RIM
                ]),
                type: "vec4<f32>"
            },
            /** The two ends of the depth ramp, as multipliers on the plate. */
            uDeepTint: { value: tint(options.deepTint, options.height), type: "vec4<f32>" },
            uCrestTint: { value: tint(options.crestTint, options.height), type: "vec4<f32>" },
            // One (kx, ky, amplitude, phase) per train, under the names the wave module declares.
            // All zeroes is a flat calm, which is what an arena with no sea yet should draw.
            ...Object.fromEntries(WAVE_UNIFORMS.map((name: string) => [name, { value: new Float32Array(4), type: "vec4<f32>" }]))
        });

        super({
            glProgram: GlProgram.from({ vertex: defaultFilterVert, fragment: glFragment, name: "ocean-filter" }),
            resources: {
                seaUniforms: uniforms,
                uTrailMap: options.trail.texture.source,
                uTrailSampler: options.trail.texture.source.style
            }
        });

        this._height = options.height;
        this._deep = options.deepTint;
        this._crest = options.crestTint;

        this._trail = options.trail;
        // In the scene only so that it has a world transform to be read. The stock filter does
        // this for its own map; nothing else will do it for this one.
        this._trail.renderable = false;
    }

    public override apply(filterManager: FilterSystem, input: Texture, output: RenderSurface, clearMode: boolean): void {
        // Filter pixels to trail pixels, every frame: the sprite is resized with the window, so
        // the matrix does not survive one. It is also how a pixel finds itself in the world.
        filterManager.calculateSpriteMatrix(this.resources.seaUniforms.uniforms.uTrailMatrix, this._trail);

        // Re-read in case a texture was swapped underneath — `Water` resizes the trail target in
        // place, but this costs nothing and is what the stock filter does.
        this.resources.uTrailMap = this._trail.texture.source;

        filterManager.applyFilter(this, input, output, clearMode);
    }

    /**
     * How many world pixels of sea the viewport covers. A hull that pulls the camera back sees more
     * water, so the same screen shows more wavelengths — which is all the zoom has to say to the
     * shader now that the waves are measured from the camera rather than from the world.
     */
    public setFrame(width: number, height: number, zoom: number): void {
        const frame = this.resources.seaUniforms.uniforms.uSeaFrame as Float32Array;
        frame[2] = width / zoom;
        frame[3] = height / zoom;
    }

    /**
     * The sea, packed for the GPU: a wave vector rather than a heading and a wavelength,
     * because the shader would otherwise redo that trigonometry per pixel for a number that
     * changes twenty times a second.
     *
     * Trains past `WAVE_TRAINS` are dropped and missing ones are zeroed, which draws a flat calm
     * rather than whatever the last frame left in the buffer.
     */
    public setWaves(sea: readonly WaveTrain[]): void {
        for (let i = 0; i < WAVE_TRAINS; i++) {
            const packed = this.resources.seaUniforms.uniforms[WAVE_UNIFORMS[i]] as Float32Array;
            const train = sea[i];

            if (!train) {
                packed.fill(0);
                continue;
            }

            const vector = waveVector(train, this._vector);
            packed[0] = vector.x;
            packed[1] = vector.y;
            packed[2] = train.amplitude;
            packed[3] = train.phase;
        }
    }

    /**
     * Re-scales everything `waveHeight` scales — the warp, the light, the white, the glint and the
     * depth ramp — plus the slope's unit, which moves with the wavelength.
     *
     * The constructor computes all of this once, because in a battle the dials never move. The
     * dashboard's workspace is the one place they do: see `setWaveDials` in Waves.ts. Written into
     * the existing buffers rather than replaced, so the uniform group the GPU already holds is the
     * one that changes.
     */
    public setSwell(height: number, capStrength: number): void {
        const uniforms = this.resources.seaUniforms.uniforms;
        this._height = height;

        const scales = uniforms.uScales as Float32Array;
        scales[0] = SWELL_WARP * height;
        scales[1] = SHADE_GAIN * height;
        scales[2] = SLOPE_UNIT;

        const surface = uniforms.uSurface as Float32Array;
        surface[2] = capStrength * height;
        surface[3] = GLINT_STRENGTH * height;

        this._writeDepth();
    }

    /**
     * The sea's colours: what the water is tinted toward in a trough and on a crest, and the one
     * white that both the wake's foam and a breaking crest are painted in.
     *
     * The tints come in as multipliers rather than as colours because that is what the shader
     * multiplies by — see the note above `OceanFilterOptions`, and `tintBetween` in
     * shared/Settings.ts for the division that turns a picked colour into one. Like `setSwell`,
     * this is the dashboard's; the constructor does the same work once for the game.
     */
    public setPalette(deep: Rgb, crest: Rgb, foamColor: number): void {
        this._deep = deep;
        this._crest = crest;

        const foam = this.resources.seaUniforms.uniforms.uFoamTint as Float32Array;
        foam[0] = ((foamColor >> 16) & 0xff) / 255;
        foam[1] = ((foamColor >> 8) & 0xff) / 255;
        foam[2] = (foamColor & 0xff) / 255;

        this._writeDepth();
    }

    /** The depth ramp, from whichever colours and height are current. Both setters end here. */
    private _writeDepth(): void {
        const uniforms = this.resources.seaUniforms.uniforms;

        writeTint(uniforms.uDeepTint as Float32Array, this._deep, this._height);
        writeTint(uniforms.uCrestTint as Float32Array, this._crest, this._height);
    }

    /** How hard the wake bends the water, in pixels. `Water` sets this once it knows the wake. */
    public set trailScale(pixels: number) {
        (this.resources.seaUniforms.uniforms.uScales as Float32Array)[3] = pixels;
    }

    /** How solid the foam reads where there is any, 0 to 1. Likewise. */
    public set foamStrength(amount: number) {
        (this.resources.seaUniforms.uniforms.uFoamEdge as Float32Array)[2] = amount;
    }
}
